import React from 'react';
import FishWaterColumn3D from '../FishWaterColumn3D/FishWaterColumn3D';
import './InfoPanel.css';

const InfoPanel = ({
  activePopup,
  selectedCellDetails,
  selectedPort,
  selectedSpecies,
  catchabilityData,
  speciesLEKData,
  movementAnalysisData,
  onClose,
  onSwitchToMigrationHeatmap,
  onSwitchToHourlyCatchability,
  onSwitchTo3DMovement,
  onSwitchToSpeciesLEK,
  onSwitchToPort
}) => {
  
  const formatCoordinate = (coord, type) => {
    if (!coord) return 'N/A';
    const value = Math.abs(parseFloat(coord));
    const direction = type === 'lat' 
      ? (parseFloat(coord) >= 0 ? 'N' : 'S')
      : (parseFloat(coord) >= 0 ? 'E' : 'W');
    return `${value.toFixed(4)}°${direction}`;
  };
  
  // Don't render if no popup is active AND no species LEK data is available
  if (!activePopup && !speciesLEKData && !selectedPort) return null;

  // If we have species LEK data but no active popup, default to species-lek tab
  const currentActivePopup = activePopup || (speciesLEKData ? 'species-lek' : null);

  const formatDateTime = (dateTimeString) => {
    const date = new Date(dateTimeString);
    date.setDate(date.getDate() + 1);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getScoreClass = (score) => {
    if (score >= 0.8) return 'score-excellent';
    if (score >= 0.6) return 'score-good';
    if (score >= 0.4) return 'score-fair';
    if (score >= 0.2) return 'score-poor';
    return 'score-very-poor';
  };

  // 3D Movement Analysis depth visualization component
  const MovementAnalysisProfile = ({ movementAnalysisData }) => {
    if (!movementAnalysisData?.terrain) return null;

    const { terrain } = movementAnalysisData;
    const { summary, } = terrain;

    return (
      <div style={{ marginTop: '15px' }}>
        
        {/* Depth statistics */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: '8px',
          fontSize: '11px',
          marginBottom: '10px'
        }}>
          <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#e3f2fd', borderRadius: '3px' }}>
            <div style={{ fontWeight: 'bold', color: '#1976d2' }}>{summary.minDepth?.toFixed(1)}ft</div>
            <div style={{ color: '#666' }}>Min Depth</div>
          </div>
          <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#e8f5e8', borderRadius: '3px' }}>
            <div style={{ fontWeight: 'bold', color: '#388e3c' }}>{summary.averageDepth?.toFixed(1)}ft</div>
            <div style={{ color: '#666' }}>Avg Depth</div>
          </div>
          <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#fff3e0', borderRadius: '3px' }}>
            <div style={{ fontWeight: 'bold', color: '#f57c00' }}>{summary.maxDepth?.toFixed(1)}ft</div>
            <div style={{ color: '#666' }}>Max Depth</div>
          </div>
        </div>

      </div>
    );
  };

  return (
    <div className="lek-info-panel">
      <div className="lek-info-title">
        {/* Tab Navigation with new names */}
        <div className="info-tab-navigation">
          {/* Species LEK Tab - Always show */}
          <button
            onClick={onSwitchToSpeciesLEK}
            className={`info-tab-button ${currentActivePopup === 'species-lek' ? 'active' : ''}`}
          >
            Species LEK
          </button>
          
          <button
            onClick={onSwitchToMigrationHeatmap}
            className={`info-tab-button ${currentActivePopup === 'migration-heatmap' ? 'active' : ''}`}
          >
            Migration Heatmap
          </button>
          
          <button
            onClick={onSwitchToHourlyCatchability}
            className={`info-tab-button ${currentActivePopup === 'hourly-catchability' ? 'active' : ''}`}
          >
            Hourly Catchability Index
          </button>

          {/* 3D Movement Analysis Tab - Only show when data exists */}
          {(movementAnalysisData || catchabilityData) && (
            <button
              onClick={onSwitchTo3DMovement}
              className={`info-tab-button ${currentActivePopup === '3d-movement' ? 'active' : ''}`}
            >
              3D Movement Analysis
            </button>
          )}

          {/* Port Tab - Show when port is selected */}
          {selectedPort && (
            <button
              onClick={onSwitchToPort}
              className={`info-tab-button ${currentActivePopup === 'port' ? 'active' : ''}`}
            >
              Port Information
            </button>
          )}

        </div>
        
        <button 
          className="close-button"
          onClick={onClose}
          aria-label="Close details panel"
        >
          ×
        </button>
      </div>
      
      <div className="lek-info-content">
        {/* Migration Heatmap Tab */}
        {currentActivePopup === 'migration-heatmap' && (
          <div>
            <h4>Migration Heatmap Details</h4>
            
            {selectedCellDetails?.loading && (
              <div className="loading">Loading details...</div>
            )}
            
            {selectedCellDetails?.error && (
              <div className="error-message">{selectedCellDetails.error}</div>
            )}
            
            {selectedCellDetails?.details?.grid_cell && (
              <div>
                <p><strong>Grid ID:</strong> {String(selectedCellDetails.details.grid_cell.grid_id)}</p>
                <p><strong>Species:</strong> {String(selectedCellDetails.details.grid_cell.common_name)}</p>
                
                {/* Enhanced coordinate display */}
                <div style={{ 
                  background: 'rgba(0, 123, 255, 0.1)', 
                  padding: '8px', 
                  borderRadius: '4px', 
                  margin: '8px 0',
                  border: '1px solid rgba(0, 123, 255, 0.3)'
                }}>
                  <p style={{ margin: '2px 0', fontFamily: 'monospace' }}>
                    <strong>📍 Location:</strong>
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'monospace', fontSize: '0.9em' }}>
                    <span>Lat: {formatCoordinate(selectedCellDetails.details.grid_cell.latitude, 'lat')}</span>
                    <span>Lon: {formatCoordinate(selectedCellDetails.details.grid_cell.longitude, 'lon')}</span>
                  </div>
                  <div style={{ fontSize: '0.8em', color: '#666', marginTop: '4px' }}>
                    Decimal: {Number(selectedCellDetails.details.grid_cell.latitude)?.toFixed(6) || 'N/A'}, {Number(selectedCellDetails.details.grid_cell.longitude)?.toFixed(6) || 'N/A'}
                  </div>
                </div>
                
                <p><strong>Area:</strong> {((Number(selectedCellDetails.details.grid_cell.area_m2) || 0) / 1000000).toFixed(2)} km²</p>
                <p><strong>Migration Probability:</strong> {Number(selectedCellDetails.details.grid_cell.base_weighted_yield)?.toFixed(3) || 'N/A'}</p>
                <p><strong>Reports:</strong> {Number(selectedCellDetails.details.grid_cell.num_reports) || 0}</p>
                <p><strong>Coverage:</strong> {Number(selectedCellDetails.details.grid_cell.coverage_pct_sum)?.toFixed(1) || 'N/A'}%</p>
                <p><strong>Confidence:</strong> {Number(selectedCellDetails.details.grid_cell.aggregate_confidence)?.toFixed(2) || 'N/A'}</p>
                
                {selectedCellDetails.details.contributing_polygons?.length > 0 && (
                  <div>
                    <h4 style={{ margin: '10px 0 5px 0', fontSize: '13px' }}>
                      Contributing Fishermen ({selectedCellDetails.details.contributing_polygons.length})
                    </h4>
                    <div className="contributing-polygons">
                      {selectedCellDetails.details.contributing_polygons.map((polygon, index) => (
                        <div key={polygon.polygon_id} className="polygon-info">
                          <p><strong>Fisherman:</strong> {polygon.fisherman_id}</p>
                          <p><strong>Yield:</strong> {polygon.yield_estimate}</p>
                          <p><strong>Confidence:</strong> {polygon.confidence.toFixed(2)}</p>
                          <p><strong>Method:</strong> {polygon.capture_method.replace('_', ' ')}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {selectedCellDetails?.details && !selectedCellDetails.details.grid_cell && (
              <p>No migration data available for this grid cell.</p>
            )}
          </div>
        )}

        {/* Hourly Catchability Index Tab */}
        {currentActivePopup === 'hourly-catchability' && (
          <div>
            <h4>Hourly Catchability Index</h4>
            
            {!catchabilityData && (
              <p style={{ color: '#666', fontStyle: 'italic' }}>
                Load analysis data from the control panel or click on a station point to see detailed hourly catchability results.
              </p>
            )}
            
            {catchabilityData && (
              <div>
                <div style={{ marginBottom: '15px' }}>
                  <p><strong>Species:</strong> {catchabilityData.species}</p>
                  <p><strong>Date Range:</strong> {catchabilityData.date_range}</p>
                  <p><strong>Fishermen:</strong> {catchabilityData.fishermen_count} ({catchabilityData.fishermen_names?.join(', ')})</p>
                </div>

                <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                  <h5 style={{ margin: '0 0 8px 0' }}>Hourly Predictions (First 100)</h5>
                  <div style={{ fontSize: '11px' }}>
                    {catchabilityData.hourly_results?.slice(0, 100).map((result, index) => (
                      <div key={index} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '4px 0',
                        borderBottom: '1px solid #eee'
                      }}>
                        <div>{formatDateTime(result.datetime)}</div>
                        <div>
                          <span className={`catchability-score ${getScoreClass(result.catchability)}`}>
                            {result.catchability.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ fontSize: '10px', color: '#666', marginTop: '10px', fontStyle: 'italic' }}>
                  Showing first 50 hours. Scores: 0.8+ excellent, 0.6+ good, 0.4+ fair, 0.2+ poor, &lt;0.2 very poor
                </div>
              </div>
            )}
          </div>
        )}

        {/* 3D Movement Analysis Tab */}
        {currentActivePopup === '3d-movement' && (
          <div>
            <h4>3D Movement Analysis</h4>
            
            {!movementAnalysisData && !catchabilityData && (
              <p style={{ color: '#666', fontStyle: 'italic' }}>
                Enable 3D Movement Analysis in the control panel and click on a grid cell to see depth profile.
              </p>
            )}
            
            {(movementAnalysisData || catchabilityData) && (
              <div>
                {/* Basic Info */}
                {movementAnalysisData && (
                  <div style={{ marginBottom: '15px' }}>
                    <p><strong>Grid ID:</strong> {movementAnalysisData.gridId}</p>
                    <p><strong>Species:</strong> {movementAnalysisData.gridInfo?.species || 'N/A'}</p>
                    <p><strong>Location:</strong> {movementAnalysisData.gridInfo?.latitude?.toFixed(4)}°N, {movementAnalysisData.gridInfo?.longitude?.toFixed(4)}°W</p>
                    <p><strong>Grid Size:</strong> 2km × 2km</p>
                  </div>
                )}

                {/* 3D Fish Water Column Visualization */}
                <FishWaterColumn3D 
                  gridCell={movementAnalysisData?.gridInfo ? {
                    grid_id: movementAnalysisData.gridId,
                    latitude: movementAnalysisData.gridInfo.latitude,
                    longitude: movementAnalysisData.gridInfo.longitude,
                    species_id: selectedSpecies || 
                               movementAnalysisData.gridInfo.species_id || 
                               catchabilityData?.species_id
                  } : null}
                />

                {/* Original depth profile if we have movement data */}
                {movementAnalysisData && (
                  <MovementAnalysisProfile movementAnalysisData={movementAnalysisData} />
                )}
              </div>
            )}
          </div>
        )}

        {currentActivePopup === 'species-lek' && (
          <div>
            {!speciesLEKData && (
              <p style={{ color: '#666', fontStyle: 'italic' }}>
                No species LEK data available. Select a species from the control panel to view detailed information.
              </p>
            )}
            
            {speciesLEKData && (
              <div>
                {/* Species Information */}
                <div style={{ marginBottom: '15px' }}>
                  <p style={{ margin: '4px 0', fontSize: '12px', color: '#666', fontWeight: 'bold' }}>
                    {speciesLEKData.common_name || 'N/A'}
                  </p>
                  <h5 style={{ margin: '0 0 8px 0', color: '#1976d2' }}>
                    {speciesLEKData.scientific_name || 'Unknown Species'}
                  </h5>
                </div>

                {/* Species Image */}
                {speciesLEKData.image_url && (
                  <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                    <img 
                      src={speciesLEKData.image_url} 
                      alt={speciesLEKData.scientific_name || 'Species'}
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '200px', 
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                    />
                  </div>
                )}

                {/* LEK Data Grid */}
                <div style={{ 
                  display: 'grid', 
                  gap: '6px',
                  fontSize: '12px'
                }}>

                  {/* Industry alignment score */}
                  {speciesLEKData.alignment && (
                    <div style={{ 
                      padding: '10px', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '6px',
                      fontSize: '11px',
                      color: '#6c757d',
                      border: '1px solid #e9ecef'
                    }}>
                      <p><b style={{ fontWeight: 'bold', color: '#495057' }}>Industry Degree of Agreement/ Disagreement with Assessment:</b> {speciesLEKData.alignment}/10</p>
                      <div style={{ fontSize: '20px', textAlign: 'center', paddingTop: '10px' }}>
                        {speciesLEKData.alignment === 10 && (<b style={{ color: '#46f536' }}>Highest Agreement</b>)}
                        {speciesLEKData.alignment >= 8 && speciesLEKData.alignment < 10 && (<b style={{ color: '#c2f536' }}>High Agreement</b>)}
                        {speciesLEKData.alignment >= 4 && speciesLEKData.alignment < 8 && (<b style={{ color: '#e8d638' }}>Moderate Agreement</b>)}
                        {speciesLEKData.alignment >= 2 && speciesLEKData.alignment < 4 && (<b style={{ color: '#f58936' }}>Low Agreement</b>)}
                        {speciesLEKData.alignment === 1 && (<b style={{ color: '#f53636' }}>Lowest Agreement</b>)}
                        </div>
                      <br />
                      <div style={{ fontStyle: 'italic' }}><b>Summary comments:</b> {speciesLEKData.commentary}</div>
                    </div>
                  )}
                  
                  {/* Bottom Type */}
                  {speciesLEKData.bottom_type && (
                    <div style={{ 
                      padding: '10px', 
                      backgroundColor: '#e8f5e8', 
                      borderRadius: '6px',
                      border: '1px solid #c8e6c9'
                    }}>
                      <div style={{ fontWeight: 'bold', color: '#388e3c', marginBottom: '4px' }}>
                        Bottom Type
                      </div>
                      <div style={{ color: '#2e7d32' }}>
                        {speciesLEKData.bottom_type}
                      </div>
                    </div>
                  )}

                  {/* Depth */}
                  {speciesLEKData.depth && (
                    <div style={{ 
                      padding: '10px', 
                      backgroundColor: '#e3f2fd', 
                      borderRadius: '6px',
                      border: '1px solid #bbdefb'
                    }}>
                      <div style={{ fontWeight: 'bold', color: '#1976d2', marginBottom: '4px' }}>
                        Depth
                      </div>
                      <div style={{ color: '#1565c0' }}>
                        {speciesLEKData.depth}
                      </div>
                    </div>
                  )}

                  {/* Tide */}
                  {speciesLEKData.tide && (
                    <div style={{ 
                      padding: '10px', 
                      backgroundColor: '#e3f2fd', 
                      borderRadius: '6px',
                      border: '1px solid #bbdefb'
                    }}>
                      <div style={{ fontWeight: 'bold', color: '#1976d2', marginBottom: '4px' }}>
                        Tide Conditions
                      </div>
                      <div style={{ color: '#1565c0' }}>
                        {speciesLEKData.tide}
                      </div>
                    </div>
                  )}

                  {/* Night/Day */}
                  {speciesLEKData.night_day && (
                    <div style={{ 
                      padding: '10px', 
                      backgroundColor: '#fff3e0', 
                      borderRadius: '6px',
                      border: '1px solid #ffcc02'
                    }}>
                      <div style={{ fontWeight: 'bold', color: '#f57c00', marginBottom: '4px' }}>
                        Time of Day
                      </div>
                      <div style={{ color: '#e65100' }}>
                        {speciesLEKData.night_day}
                      </div>
                    </div>
                  )}

                  {/* Moon Phase */}
                  {speciesLEKData.moon_phase && (
                    <div style={{ 
                      padding: '10px', 
                      backgroundColor: '#f3e5f5', 
                      borderRadius: '6px',
                      border: '1px solid #e1bee7'
                    }}>
                      <div style={{ fontWeight: 'bold', color: '#7b1fa2', marginBottom: '4px' }}>
                        Moon Phase
                      </div>
                      <div style={{ color: '#6a1b9a' }}>
                        {speciesLEKData.moon_phase}
                      </div>
                    </div>
                  )}

                </div>
              </div>
            )}
          </div>
        )}

        {currentActivePopup === 'port' && selectedPort && (
          <div>
            <h3 style={{ margin: '0 0 15px 0', color: '#2c3e50', fontSize: '16px' }}>
              {selectedPort.name}
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
              <div style={{
                padding: '8px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                border: '1px solid #dee2e6'
              }}>
                <div style={{ fontWeight: 'bold', color: '#495057', marginBottom: '4px' }}>
                  State
                </div>
                <div style={{ color: '#6c757d' }}>
                  {selectedPort.state}
                </div>
              </div>
              
              <div style={{
                padding: '8px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                border: '1px solid #dee2e6'
              }}>
                <div style={{ fontWeight: 'bold', color: '#495057', marginBottom: '4px' }}>
                  Created
                </div>
                <div style={{ color: '#6c757d' }}>
                  {selectedPort.created_date ? new Date(selectedPort.created_date).toLocaleDateString() : 'N/A'}
                </div>
              </div>
            </div>

            {/* Port Notes */}
            {/* {selectedPort.notes && (
              <div style={{
                padding: '10px',
                backgroundColor: '#e3f2fd',
                borderRadius: '4px',
                border: '1px solid #2196f3',
                marginBottom: '15px'
              }}>
                <div style={{ fontWeight: 'bold', color: '#1976d2', marginBottom: '5px' }}>
                  Port Information
                </div>
                <div style={{ color: '#1565c0', fontSize: '14px', lineHeight: '1.4' }}>
                  {selectedPort.notes}
                </div>
              </div>
            )} */}

            {/* Economic Data */}
            <div style={{ marginBottom: '15px' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#495057', fontSize: '14px' }}>
                Economic Data
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div style={{
                  padding: '6px',
                  backgroundColor: '#fff3cd',
                  borderRadius: '3px',
                  border: '1px solid #ffeaa7'
                }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#856404' }}>
                    Dockage Fees
                  </div>
                  <div style={{ fontSize: '11px', color: '#6c757d' }}>
                    ${selectedPort.dockage_port_fees?.toFixed(2) || 'N/A'}
                  </div>
                </div>
                
                <div style={{
                  padding: '6px',
                  backgroundColor: '#fff3cd',
                  borderRadius: '3px',
                  border: '1px solid #ffeaa7'
                }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#856404' }}>
                    Fuel Price/L
                  </div>
                  <div style={{ fontSize: '11px', color: '#6c757d' }}>
                    ${selectedPort.fuel_price_per_l?.toFixed(3) || 'N/A'}
                  </div>
                </div>
                
                <div style={{
                  padding: '6px',
                  backgroundColor: '#fff3cd',
                  borderRadius: '3px',
                  border: '1px solid #ffeaa7'
                }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#856404' }}>
                    Ice Cost
                  </div>
                  <div style={{ fontSize: '11px', color: '#6c757d' }}>
                    ${selectedPort.ice_cost_total?.toFixed(2) || 'N/A'}
                  </div>
                </div>
                
                <div style={{
                  padding: '6px',
                  backgroundColor: '#fff3cd',
                  borderRadius: '3px',
                  border: '1px solid #ffeaa7'
                }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#856404' }}>
                    Insurance
                  </div>
                  <div style={{ fontSize: '11px', color: '#6c757d' }}>
                    ${selectedPort.insurance?.toFixed(2) || 'N/A'}
                  </div>
                </div>
              </div>
            </div>

            {/* Species Pricing */}
            <div>
              <h4 style={{ margin: '0 0 10px 0', color: '#495057', fontSize: '14px' }}>
                Species Pricing (per lb)
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', maxHeight: '200px', overflowY: 'auto' }}>
                {Object.entries(selectedPort)
                  .filter(([key, value]) => key.includes('_price_per_lb') && value !== null && value !== undefined)
                  .map(([key, value]) => {
                    const speciesName = key.replace('_price_per_lb', '').replace(/_/g, ' ').toUpperCase();
                    return (
                      <div key={key} style={{
                        padding: '4px 6px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '3px',
                        border: '1px solid #dee2e6',
                        fontSize: '11px'
                      }}>
                        <div style={{ fontWeight: 'bold', color: '#495057' }}>
                          {speciesName}
                        </div>
                        <div style={{ color: '#6c757d' }}>
                          ${value?.toFixed(2) || 'N/A'}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InfoPanel;